import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {Alert, Button, Checkbox, FlexColumn, FlexRow, RadioGroupHandler, Text} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import Styled from 'styled-components/macro';
import Util from '../../../shared/util/util';
import UpdateProjectPortalConfigMutation from '../../../../mutations/update_project_portal_config_mutation';

/**
 * Project Settings → Client Portal tab. The ONLY project-level portal controls:
 * "Show in client workspace" (publishes this project to the client's workspace) +
 * phase-status visibility. Access configuration (activation, branding, feature
 * flags) is Client Organisation level, NOT here — the notice at the top says so and
 * points the reader at the client's Workspace tab.
 *
 * Both controls are edited locally and committed on an explicit Save, so a
 * half-made decision is never published to the client mid-thought. Cancel reverts
 * to what the server last gave us.
 *
 * Writes go through the portal proxy mutation keyed by the project's id — the same
 * identifier the PSA feed syncs as psa_project_id, so a published project and its
 * synced row meet on one value. The project's company-facing number rides along as
 * `legacyPsaProjectId`: this write once used that as the key, and forwarding it lets
 * the workspace move an already-published project's row onto the id everything else
 * matches instead of stranding it. No forecast_db write.
 *
 * The client and the project's own name/status ride along on every write. The portal
 * has no row for either until a sync has run for the firm, and publishing is usually
 * the first portal action taken for a project, so it provisions both from this
 * metadata on first touch. A project with NO client cannot be published at all —
 * there is no client workspace to publish it into — so the controls are disabled
 * rather than left clickable against a write the portal would refuse.
 */

// Radio values are the two strings the group switches between; the boolean the
// mutation wants is derived at the edge so the group only ever holds one of these.
const SHOW_ON = 'on';
const SHOW_OFF = 'off';

const SettingsCard = Styled.div`
	border: 1px solid ${props => props.theme.sys.color.border.decorative};
	border-radius: 8px;
	background-color: ${props => props.theme.sys.color.background.neutral['1']};
	overflow: hidden;
`;

// Card header carries the section name and, where the setting needs it, a sentence
// of explanation. The hairline under it separates head from the control rows.
const CardHead = Styled.div`
	padding: 16px 20px;
	border-bottom: 1px solid ${props => props.theme.sys.color.border.decorative};
`;

const CardTitle = Styled.div`
	${props => props.theme.sys.font.subheading['1']};
	color: ${props => props.theme.sys.color.text.content.main};
`;

const CardBody = Styled.div`
	display: flex;
	flex-direction: column;
	gap: 10px;
	padding: 18px 20px;
`;

const RowLabel = Styled.div`
	${props => props.theme.sys.font.subheading['2']};
	color: ${props => props.theme.sys.color.text.content.main};
	margin-bottom: 4px;
`;

const RowHelp = Styled.div`
	${props => props.theme.sys.font.body['2']};
	color: ${props => props.theme.sys.color.text.content.auxiliary};
`;

// The selected option is drawn with the brand border and a tinted fill so the
// choice is legible without relying on the radio dot alone.
const RadioCard = Styled.label`
	display: flex;
	align-items: flex-start;
	gap: 12px;
	padding: 14px 16px;
	border: 1.5px solid ${({theme, $selected}) => ($selected ? theme.sys.color.border.active : theme.sys.color.border.decorative)};
	border-radius: 8px;
	background-color: ${({theme, $selected}) =>
		$selected ? theme.sys.color.background.primary['1'] : theme.sys.color.background.neutral['1']};
	cursor: ${({$disabled}) => ($disabled ? 'default' : 'pointer')};
`;

const FooterBar = Styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
`;

// Says where a saved change lands and when the client will see it, so nobody has to
// guess whether Save reached the workspace.
const AuditNote = Styled.div`
	${props => props.theme.sys.font.detail['1']};
	color: ${props => props.theme.sys.color.text.content.auxiliary};
`;

const ProjectClientPortal = ({
	psaProjectId,
	legacyPsaProjectId,
	portalConfig,
	psaClientExternalId,
	clientName,
	projectName,
	projectStatus,
}) => {
	const {formatMessage} = useIntl();
	const initialShowInClientPortal = !!portalConfig?.showInClientPortal;
	const initialPhaseStatusVisible =
		portalConfig?.portalPhaseStatusVisible == null ? true : !!portalConfig.portalPhaseStatusVisible;

	/**
	 * What we know the server holds. Cancel returns here, and the dirty check compares
	 * against it, so a control flipped and flipped back saves nothing.
	 *
	 * It has to be state that a successful save advances, NOT a value derived from
	 * `portalConfig`, because the mutation's response can never refresh that prop:
	 * ProjectPortalConfig carries no id, so Relay normalises the payload into a record
	 * of its own rather than merging it into the project's, and the mutation passes our
	 * own socketClientId, which suppresses the refetch for the tab that wrote. Derived
	 * from the prop, the form would stay dirty forever after saving — and Cancel would
	 * then revert to the pre-save value, ready to publish or unpublish it a second time.
	 */
	const [saved, setSaved] = useState({
		showInClientPortal: initialShowInClientPortal,
		phaseStatusVisible: initialPhaseStatusVisible,
	});
	const [showInClientPortal, setShowInClientPortal] = useState(initialShowInClientPortal);
	const [phaseStatusVisible, setPhaseStatusVisible] = useState(initialPhaseStatusVisible);
	const hasClient = !!psaClientExternalId;
	const isDirty = showInClientPortal !== saved.showInClientPortal || phaseStatusVisible !== saved.phaseStatusVisible;

	const onCancel = () => {
		setShowInClientPortal(saved.showInClientPortal);
		setPhaseStatusVisible(saved.phaseStatusVisible);
	};

	const onSave = () => {
		// Nothing to write, and a project with no client has no workspace to publish
		// into — the portal would refuse the write, so don't make it.
		if (!hasClient || !isDirty) return;
		const next = {showInClientPortal, phaseStatusVisible};
		Util.CommitMutation(
			UpdateProjectPortalConfigMutation,
			{
				psaProjectId: String(psaProjectId),
				...(legacyPsaProjectId == null ? {} : {legacyPsaProjectId: String(legacyPsaProjectId)}),
				showInClientPortal: next.showInClientPortal,
				portalPhaseStatusVisible: next.phaseStatusVisible,
				psaClientExternalId,
				// Omitted when absent so the portal keeps whatever name it already has,
				// rather than overwriting it with an empty string.
				...(clientName ? {clientName} : {}),
				...(projectName ? {projectName} : {}),
				...(projectStatus ? {projectStatus} : {}),
			},
			// Only on success: a failed write leaves the form dirty so the edit is still
			// there to retry, rather than being quietly adopted as the server's value.
			() => setSaved(next)
		);
	};

	const visibilityOptions = [
		{
			value: SHOW_ON,
			label: formatMessage({id: 'common.on'}),
			description: formatMessage({id: 'client_portal.show_in_portal_on_desc'}),
		},
		{
			value: SHOW_OFF,
			label: formatMessage({id: 'common.off'}),
			description: formatMessage({id: 'client_portal.show_in_portal_off_desc'}),
		},
	];

	return (
		<FlexColumn gap={'l'} data-cy="project-client-portal-tab">
			{hasClient ? null : (
				<Text color={'secondary'} data-cy="no-client-assigned-notice">
					<FormattedMessage id="client_portal.no_client_assigned" />
				</Text>
			)}

			{/* Portal access is a per-customer decision; this tab only publishes one
			    project. Saying so here is what stops someone hunting for branding and
			    feature flags that deliberately do not live on a project. */}
			<Alert type={Alert.TYPE.INFORMATION} text={formatMessage({id: 'client_portal.access_on_customer_notice'})} />

			<SettingsCard>
				<CardHead>
					<CardTitle>
						<FormattedMessage id="client_portal.show_in_portal" />
					</CardTitle>
					<RowHelp>
						<FormattedMessage id="client_portal.show_in_portal_help" />
					</RowHelp>
				</CardHead>
				<CardBody>
					<RadioGroupHandler
						value={showInClientPortal ? SHOW_ON : SHOW_OFF}
						disabled={!hasClient}
						onValueChange={value => setShowInClientPortal(value === SHOW_ON)}
					>
						{visibilityOptions.map(option => {
							const selected = (showInClientPortal ? SHOW_ON : SHOW_OFF) === option.value;
							return (
								<RadioCard key={option.value} $selected={selected} $disabled={!hasClient}>
									<RadioGroupHandler.Item
										value={option.value}
										data-cy={`show-in-client-portal-${option.value}`}
									/>
									<div>
										<RowLabel>{option.label}</RowLabel>
										<RowHelp>{option.description}</RowHelp>
									</div>
								</RadioCard>
							);
						})}
					</RadioGroupHandler>
				</CardBody>
			</SettingsCard>

			<SettingsCard>
				<CardHead>
					<CardTitle>
						<FormattedMessage id="client_portal.phase_statuses_title" />
					</CardTitle>
				</CardHead>
				<CardBody>
					{/* Checkbox rather than the design's toggle switch: the design-system
					    Toggle hardcodes aria-label="toggle" and spreads no extra props, so it
					    cannot carry an accessible name. Checkbox names itself from labelText
					    and renders the same explanation through `description`. */}
					<Checkbox
						checked={phaseStatusVisible}
						disabled={!hasClient}
						onChange={() => setPhaseStatusVisible(previous => !previous)}
						data-cy="phase-status-visible-toggle"
						labelText={formatMessage({id: 'client_portal.phase_status_visible'})}
						description={formatMessage({id: 'client_portal.phase_status_visible_help'})}
					/>
				</CardBody>
			</SettingsCard>

			<FooterBar>
				<AuditNote>
					<FormattedMessage id="client_portal.project_settings_audit_note" />
				</AuditNote>
				<FlexRow gap={'s'}>
					<Button emphasis="low" disabled={!isDirty} onPress={onCancel} data-cy="project-portal-cancel">
						<FormattedMessage id="common.cancel" />
					</Button>
					<Button disabled={!hasClient || !isDirty} onPress={onSave} data-cy="project-portal-save">
						<FormattedMessage id="common.save_changes" />
					</Button>
				</FlexRow>
			</FooterBar>
		</FlexColumn>
	);
};

ProjectClientPortal.propTypes = {
	psaProjectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	// The project's company-facing number. Only used to find config this project may
	// still be stored under; omitting it just means no such row can be recovered.
	legacyPsaProjectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	portalConfig: PropTypes.shape({
		showInClientPortal: PropTypes.bool,
		portalPhaseStatusVisible: PropTypes.bool,
	}),
	// The client's Relay global id; the proxy decodes it to the raw Forecast client
	// id the portal keys client orgs by. Absent for a project with no client.
	psaClientExternalId: PropTypes.string,
	clientName: PropTypes.string,
	projectName: PropTypes.string,
	projectStatus: PropTypes.string,
};

export default ProjectClientPortal;
